/**
 * @fileoverview Main JavaScript entry point for Saman Gostaran Mana corporate website.
 * Implements Anti-Gravity animation engine using GSAP and ScrollTrigger.
 * @author Senior Frontend Developer & UI/UX Expert
 * @version 1.0.0
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP Plugins
gsap.registerPlugin(ScrollTrigger);

/**
 * Initializes anti-gravity floating animations for the Hero section.
 * Causes satellite cards and background elements to softly bob with slight phase offsets.
 * @function initHeroFloatingElements
 * @returns {void}
 */
function initHeroFloatingElements() {
  const floatingCards = document.querySelectorAll('.floating-card');

  floatingCards.forEach((card, index) => {
    const floatSpeed = parseFloat(card.dataset.speed || '1');
    const delay = index * 0.4;
    const yOffset = 14 * floatSpeed;
    const xOffset = 6 * (index % 2 === 0 ? 1 : -1);

    gsap.to(card, {
      y: `+=${yOffset}`,
      x: `+=${xOffset}`,
      rotation: index % 2 === 0 ? 1.5 : -1.5,
      duration: 3 + index * 0.7,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      delay: delay
    });
  });

  // Soft ambient drift for background orbs
  gsap.to('.ambient-canvas__orb--green', {
    x: 40,
    y: 60,
    scale: 1.1,
    duration: 8,
    repeat: -1,
    yoyo: true,
    ease: 'sine.inOut'
  });

  gsap.to('.ambient-canvas__orb--tahini', {
    x: -30,
    y: -40,
    scale: 0.95,
    duration: 7,
    repeat: -1,
    yoyo: true,
    ease: 'sine.inOut'
  });
}

/**
 * Configures ScrollTrigger parallax effects for the Hero and Services sections.
 * Gives a weightless, multi-layered depth feeling during scrolling.
 * @function initScrollParallax
 * @returns {void}
 */
function initScrollParallax() {
  // Hero elements parallax entrance and drift
  gsap.from('.hero__content > *', {
    opacity: 0,
    y: 35,
    stagger: 0.12,
    duration: 1.1,
    ease: 'power3.out'
  });

  gsap.from('.hero__visual', {
    opacity: 0,
    scale: 0.88,
    duration: 1.4,
    ease: 'power2.out',
    delay: 0.2
  });

  // Services section header reveal
  gsap.from('.services__header > *', {
    scrollTrigger: {
      trigger: '.services',
      start: 'top 80%',
      toggleActions: 'play none none none'
    },
    opacity: 0,
    y: 40,
    stagger: 0.15,
    duration: 0.9,
    ease: 'power3.out'
  });

  // Anti-gravity staggered elevation for service cards
  const cards = gsap.utils.toArray('.service-card');
  cards.forEach((card, i) => {
    const depth = parseFloat(card.getAttribute('data-depth') || '0.3');

    // Scroll entry animation
    gsap.from(card, {
      scrollTrigger: {
        trigger: card,
        start: 'top 88%',
        toggleActions: 'play none none none'
      },
      opacity: 0,
      y: 60 + i * 15,
      duration: 1,
      ease: 'power3.out',
      delay: (i % 3) * 0.15
    });

    // Continuous anti-gravity vertical parallax on scroll
    gsap.to(card, {
      scrollTrigger: {
        trigger: card,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1.2
      },
      y: -40 * depth,
      ease: 'none'
    });
  });
}

/**
 * Attaches interactive 3D perspective tilt to elements with [data-tilt].
 * Creates a dynamic reactive surface that responds to mouse cursor coordinates.
 * @function initInteractiveCardTilt
 * @returns {void}
 */
function initInteractiveCardTilt() {
  const tiltCards = document.querySelectorAll('[data-tilt]');

  tiltCards.forEach((card) => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      const rotateX = ((y - centerY) / centerY) * -7;
      const rotateY = ((x - centerX) / centerX) * 7;

      gsap.to(card, {
        rotationX: rotateX,
        rotationY: rotateY,
        transformPerspective: 1000,
        ease: 'power1.out',
        duration: 0.4
      });
    });

    card.addEventListener('mouseleave', () => {
      gsap.to(card, {
        rotationX: 0,
        rotationY: 0,
        ease: 'power2.out',
        duration: 0.6
      });
    });
  });
}

/**
 * Main application initialization triggered once DOM is loaded.
 * @function bootstrap
 * @returns {void}
 */
function bootstrap() {
  initHeroFloatingElements();
  initScrollParallax();
  initInteractiveCardTilt();
}

// Ensure execution after DOM content is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
